/* eslint-disable no-console */
import React, { Component } from 'react';
import { injectIntl, FormattedMessage } from 'react-intl';
import { LayoutWithStepper } from 'layouts';
import { connect } from 'react-redux';
import { toast } from 'react-toastify';
import {
  idPassportAction, stepAction, applicantAction, personalInformation,
} from 'shared/store/actions';
import selectors from 'shared/store/selectors';
import apiRequest from 'shared/services/api';
import paci from 'shared/helpers/paci';
import {
  Input, PaddedContainer, FooterAction, PageContentWrapper,
  FormElementContainer, Label, Options, Button,
} from 'components';
import {
  PATH, FORM_KEYS, APPLICANT_HISTORY_CODE,
} from 'shared/constant';
import validation from 'shared/validations';
import {
  isIOS, isMobileOnly,
} from 'react-device-detect';
import './IdPassport.scss';

/** Log the raw PACI responses on the console, to check the data format while testing */
const isPaciDebug = process.env.REACT_APP_PACI_DEBUG === 'true';

/** PACI sends the data to the backend once the user approves, so the result is polled until the timeout */
const PACI_POLL_DELAY = 30 * 1000;
const PACI_POLL_INTERVAL = 15 * 1000;
const PACI_POLL_TIMEOUT = 5 * 60 * 1000;

const wait = ms => new Promise(resolve => setTimeout(resolve, ms));

class IdPassport extends Component {
  state = {
    isPaciLoading: false,
    isPaciPolling: false,
    isPaciFailed: false,
    paciRedirectUrl: '',
  };

  componentDidMount() {
    const {
      isAllStepsCompleted, onUpdateCurrentStep, history,
    } = this.props;

    if (isAllStepsCompleted) {
      history.push(PATH.HOME);
    } else {
      onUpdateCurrentStep('step2');
    }
  }

  componentWillUnmount() {
    this.isUnmounted = true;
  }

  onElementChange = ({ target: { name, value } }) => {
    const {
      onUpdateIdPassport,
    } = this.props;

    if (name === FORM_KEYS.ID_PASSPORT.IS_WITH_CIVIL_ID && value === 'no') {
      onUpdateIdPassport(FORM_KEYS.ID_PASSPORT.CIVIL_ID, '');
    } else if (name === FORM_KEYS.ID_PASSPORT.IS_WITH_CIVIL_ID && value === 'yes') {
      onUpdateIdPassport(FORM_KEYS.ID_PASSPORT.PASSPORT_ID, '');
    }
    onUpdateIdPassport(name, value);
  }

  /** Generate the PACI QR request, open the QR link on a new tab then poll the backend for the result */
  onVerifyWithPaci = async (isCallbackApi = true) => {
    const {
      intl, token, phoneNumber, applicantId,
    } = this.props;
    this.setState({ isPaciLoading: true, isPaciFailed: false, paciRedirectUrl: '' });

    /**
     * Opened before the request since browsers block new tabs that are not opened right on click.
     * Skipped on iOS since Safari blocks it, or switches to the blank tab before the QR link is set
     */
    const qrTab = isIOS ? null : window.open('', '_blank');
    const tempCallback = 'https://cobmo-api-staging.newwealth.cloud/smart-client-data-api/test/qr';
    const callbackEndpoint = isCallbackApi ? tempCallback : paci.getCallbackUrl(PATH.ID_PASSPORT);
    let res;
    let requestId;
    try {
      res = await apiRequest.generatePaciQr(token, phoneNumber, applicantId, callbackEndpoint); //paci.getCallbackUrl(PATH.ID_PASSPORT)
      if (isPaciDebug) console.log('[PACI] QR response', res);

      const qrResponse = paci.extractQrResponse(res);
      ({ requestId } = qrResponse);
      if (!qrResponse.redirectUrl) throw new Error('No redirectUrl found on the response');
      if (!requestId) throw new Error('No requestId found on the response');

      if (qrTab) qrTab.location.href = qrResponse.redirectUrl;
      /** No tab opened (iOS or pop-up blocked), the user taps the open link instead which browsers always allow */
      else this.setState({ paciRedirectUrl: qrResponse.redirectUrl });
    } catch (error) {
      if (isPaciDebug) console.log('[PACI] QR error', error);
      if (qrTab) qrTab.close();
      toast.error(intl.formatMessage({ id: 'step.idPassport.paci.error' }));
      this.setState({
        isPaciLoading: false,
        isPaciFailed: true,
      });
      return;
    }

    this.onGetPaciResult(requestId);
  }

  /** Wait for the user to approve on the Kuwait Mobile ID app, then fill in the PACI data */
  onGetPaciResult = async (requestId) => {
    const {
      intl, onUpdateIdPassport, onUpdateClientDetails, onSaveApplicant, onSavePaciFrontStatus,
    } = this.props;
    const { ID_PASSPORT, CLIENT_DETAILS } = FORM_KEYS;
    this.setState({ isPaciPolling: true });

    const pollResponse = await this.onPollPaciResult(requestId);
    if (this.isUnmounted) return;

    const {
      civilId, fullName, nationality, dateOfBirth,
    } = paci.mapPaciData(pollResponse);
    const paciFrontStatus = {
      updateDate: Math.floor(Date.now() / 1000),
      requestId,
      /** PACI only sends the data to the backend after the QR is scanned and approved */
      qrScanned: !!pollResponse,
      getInfoSuccess: !!civilId,
    };

    if (civilId) {
      onUpdateIdPassport(ID_PASSPORT.IS_WITH_CIVIL_ID, 'yes');
      onUpdateIdPassport(ID_PASSPORT.PASSPORT_ID, '');
      onUpdateIdPassport(ID_PASSPORT.CIVIL_ID, civilId);
      if (fullName) onUpdateIdPassport(ID_PASSPORT.FULL_NAME, fullName);
      if (nationality) onUpdateClientDetails(CLIENT_DETAILS.NATIONALITY, nationality);
      if (dateOfBirth) onUpdateClientDetails(CLIENT_DETAILS.DATE_OF_BIRTH, dateOfBirth);
    } else {
      toast.error(intl.formatMessage({ id: 'step.idPassport.paci.error' }));
    }
    onSavePaciFrontStatus(paciFrontStatus);
    onSaveApplicant(false);

    this.setState({
      isPaciLoading: false,
      isPaciPolling: false,
      isPaciFailed: !civilId,
      paciRedirectUrl: '',
    });
  }

  /** Poll the backend until it has the PACI data; returns null if there is none before the timeout */
  onPollPaciResult = async (requestId) => {
    const { token, phoneNumber } = this.props;
    const timeoutAt = Date.now() + PACI_POLL_TIMEOUT;

    await wait(PACI_POLL_DELAY);
    while (!this.isUnmounted && Date.now() <= timeoutAt) {
      try {
        // eslint-disable-next-line no-await-in-loop
        const res = await apiRequest.pollPaciResult(token, phoneNumber, requestId);
        if (isPaciDebug) console.log('[PACI] poll response', res);
        if (paci.isPaciResultFinal(res)) return res;
      } catch (error) {
        if (isPaciDebug) console.log('[PACI] poll error', error);
      }
      if (Date.now() + PACI_POLL_INTERVAL > timeoutAt) break;
      // eslint-disable-next-line no-await-in-loop
      await wait(PACI_POLL_INTERVAL);
    }
    return null;
  }

  onContinue = async () => {
    const {
      history, onSaveApplicant, intl, fullName, accountName, onUpdateBankDetails, onUpdateSteps, onUpdateHistory,
    } = this.props;

    /** Save fullname as bank account name if bank account name is still empty */
    if (fullName && (!accountName || accountName === '')) await onUpdateBankDetails(FORM_KEYS.BANK.ACCOUNT_NAME, fullName);

    onSaveApplicant(false);
    onUpdateSteps(intl);
    onUpdateHistory(APPLICANT_HISTORY_CODE.SUBMIT_NAME.code, APPLICANT_HISTORY_CODE.SUBMIT_NAME.codeI18n);
    history.push(PATH.ID_PASSPORT_SCANNING);
  }

  render() {
    const {
      fullName, civilId, passportId, intl, isWithCivilId, paciFrontStatus,
    } = this.props;
    const {
      isPaciLoading, isPaciPolling, isPaciFailed, paciRedirectUrl,
    } = this.state;
    const { ID_PASSPORT } = FORM_KEYS;
    const pageValidation = {
      [ID_PASSPORT.FULL_NAME]: {
        isValid: validation.idPassportSchema(intl, ID_PASSPORT.FULL_NAME).isValidSync({ fullName }),
        errorMessage: validation.getErrorMessages({ fullName }, validation.idPassportSchema(intl, ID_PASSPORT.FULL_NAME)),
      },
      [ID_PASSPORT.CIVIL_ID]: {
        isValid: validation.idPassportSchema(intl, ID_PASSPORT.CIVIL_ID, isWithCivilId).isValidSync({ civilId }),
        errorMessage: validation.getErrorMessages({ civilId }, validation.idPassportSchema(intl, ID_PASSPORT.CIVIL_ID, isWithCivilId)),
      },
      [ID_PASSPORT.PASSPORT_ID]: {
        isValid: validation.idPassportSchema(intl, ID_PASSPORT.PASSPORT_ID, isWithCivilId).isValidSync({ passportId }),
        errorMessage: validation.getErrorMessages({ passportId }, validation.idPassportSchema(intl, ID_PASSPORT.PASSPORT_ID, isWithCivilId)),
      },
    };

    /** Civil ID details are only filled in via PACI, verified fields are read-only */
    const hasCivilIdDetails = !!civilId;
    const isPaciVerified = hasCivilIdDetails && !!(paciFrontStatus && paciFrontStatus.getInfoSuccess);

    const fullNameInput = (
      <Input
        isTranslate
        label="step.idPassport.enterFullName"
        placeholder="step.idPassport.enterFullNamePlaceholder"
        name={ID_PASSPORT.FULL_NAME}
        onInputChange={this.onElementChange}
        value={fullName}
        readOnly={isWithCivilId === 'yes' && isPaciVerified && !!fullName}
        isValid={pageValidation[ID_PASSPORT.FULL_NAME].isValid}
        errorMessage={pageValidation[ID_PASSPORT.FULL_NAME].errorMessage}
      />
    );

    let paciButtonLabel = 'step.idPassport.paci.verify';
    if (isPaciLoading) paciButtonLabel = 'step.idPassport.paci.loading';
    else if (isPaciFailed) paciButtonLabel = 'step.idPassport.paci.retry';

    const paciButton = (
      <Button
        type="button"
        isPrimary
        isDisabled={isPaciLoading}
        value={intl.formatMessage({ id: paciButtonLabel })}
        onButtonClick={this.onVerifyWithPaci}
        style={{ paddingLeft: '16px', paddingRight: '16px' }}
      />
    );

    const paciOpenLink = !!paciRedirectUrl && (
      <a
        className="nw-button nw-button--primary id-passport__paci-open"
        href={paciRedirectUrl}
        target="_blank"
        rel="noopener noreferrer"
      >
        {intl.formatMessage({ id: 'step.idPassport.paci.open' })}
      </a>
    );

    // const paciButtonTestCallback = (
    //   <Button
    //     type="button"
    //     isPrimary
    //     isDisabled={isPaciLoading}
    //     value={'Test with COB front callback'}
    //     onButtonClick={() => this.onVerifyWithPaci(false)}
    //     style={{ paddingLeft: '16px', paddingRight: '16px' }}
    //   />
    // );

    const footer = (
      <FooterAction
        isSingleButton
        rightButton={intl.formatMessage({ id: 'saveContinue' })}
        isDisabled={isPaciLoading || !(pageValidation[ID_PASSPORT.FULL_NAME].isValid && ((pageValidation[ID_PASSPORT.CIVIL_ID].isValid && civilId !== '') || (pageValidation[ID_PASSPORT.PASSPORT_ID].isValid && passportId !== '')))}
        onRightButtonClick={() => this.onContinue()}
      />
    );
    return (
      <>
        <PageContentWrapper isWithPadding isWithHeader>
          <div className="content-container">
            <PaddedContainer isRtl={this.props.appLanguage.locale === 'AR'}>
              <FormElementContainer>
                <Label label={intl.formatMessage({ id: 'step.idPassport.isWithCivilId' })} />
                <Options
                  isMultpleElements
                  type="radio"
                  hasContainer
                  name={ID_PASSPORT.IS_WITH_CIVIL_ID}
                  optionList={[{
                    id: 'yes',
                    label: 'yes',
                  }, {
                    id: 'no',
                    label: 'no',
                  }]}
                  onCheckMarkChange={this.onElementChange}
                  defaultValue={isWithCivilId}
                />
              </FormElementContainer>

              {isWithCivilId === 'yes' ? (
                <>
                  {!hasCivilIdDetails ? (
                    <FormElementContainer>
                      <p className="id-passport__paci-note"><FormattedMessage id="step.idPassport.paci.instruction" /></p>
                      {isPaciPolling && (
                        <p className="id-passport__paci-note"><FormattedMessage id="step.idPassport.paci.waiting" /></p>
                      )}
                      {paciButton}
                      {paciOpenLink}
                    </FormElementContainer>
                  ) : (
                    <>
                      {isPaciVerified && (
                        <p className="id-passport__paci-verified"><FormattedMessage id="step.idPassport.paci.verified" /></p>
                      )}
                      {fullNameInput}
                      <Input
                        isTranslate
                        label="step.idPassport.enterCivilId"
                        placeholder="step.idPassport.enterCivilId"
                        name={ID_PASSPORT.CIVIL_ID}
                        onInputChange={this.onElementChange}
                        value={civilId}
                        readOnly={isPaciVerified}
                        isValid={pageValidation[ID_PASSPORT.CIVIL_ID].isValid}
                        errorMessage={pageValidation[ID_PASSPORT.CIVIL_ID].errorMessage}
                      />
                      <Button
                        type="button"
                        isLink
                        isDisabled={isPaciLoading}
                        value={intl.formatMessage({ id: 'step.idPassport.paci.verifyAgain' })}
                        onButtonClick={this.onVerifyWithPaci}
                      />
                      {paciOpenLink}
                    </>
                  )}
                </>
              ) : (
                <>
                  <br />
                  {fullNameInput}
                  <Input
                    isTranslate
                    label="step.idPassport.enterPassportId"
                    placeholder="step.idPassport.enterPassportId"
                    name={ID_PASSPORT.PASSPORT_ID}
                    onInputChange={this.onElementChange}
                    value={passportId}
                    isValid={pageValidation[ID_PASSPORT.PASSPORT_ID].isValid}
                    errorMessage={pageValidation[ID_PASSPORT.PASSPORT_ID].errorMessage}
                  />
                </>
              )}
            </PaddedContainer>

          </div>
          {!isMobileOnly && footer}
        </PageContentWrapper>
        {isMobileOnly && footer}
      </>
    );
  }
}

const mapStateToProps = state => ({
  appLanguage: selectors.getLanguage(state),
  steps: state.stepsReducer.steps,
  fullName: selectors.getFullName(state),
  isWithCivilId: selectors.getIsWithCivilId(state),
  civilId: selectors.getCivilId(state),
  passportId: selectors.getPassportId(state),
  accountName: selectors.getAccountName(state),
  isAllStepsCompleted: selectors.isAllStepsCompleted(state),
  token: selectors.getToken(state),
  phoneNumber: selectors.getPhoneNumber(state),
  applicantId: selectors.getApplicantId(state),
  paciFrontStatus: selectors.getPaciFrontStatus(state),
});
const mapDispatchToProps = dispatch => ({
  onUpdateIdPassport: (name, value) => {
    dispatch(idPassportAction.updateIdPassportData(name, value));
  },
  onUpdateCurrentStep: currentStep => dispatch(stepAction.updateCurrentStep(currentStep)),
  onSaveApplicant: isGenerate => dispatch(applicantAction.updateApplicant(isGenerate)),
  onSavePaciFrontStatus: paciFrontStatus => dispatch(applicantAction.savePaciFrontStatus(paciFrontStatus)),
  onUpdateSteps: intl => dispatch(stepAction.updateSteps(intl)),
  onUpdateHistory: (code, codeI18n) => dispatch(applicantAction.updateHistory(code, codeI18n)),
  onUpdateBankDetails: (name, value) => {
    dispatch(personalInformation.updateBankDetails(name, value));
  },
  onUpdateClientDetails: (name, value) => {
    dispatch(personalInformation.updateClientDetails(name, value));
  },
});

export default connect(mapStateToProps, mapDispatchToProps)(LayoutWithStepper(injectIntl(IdPassport)));
